/**
 * Conversations client API — thin wrappers over the generated SDK.
 * Spec 57 Phase 4a + 4b (members + attachments).
 * Spec 66 Phase F — typing emit helper added.
 */
import { unwrapGenerated } from './generated.js';
import {
  listConversations,
  createConversation,
  startConversationFromMessage,
  getConversation,
  updateConversation,
  deleteConversation,
  listConversationGroups,
  createConversationGroup,
  deleteConversationGroup,
  replyToConversation,
  closeContextPanel,
  listConversationMessages,
  listConversationMembers,
  inviteToConversation,
  removeConversationMember,
  listConversationAttachments,
  attachToConversation,
  detachFromConversation,
  setConversationTyping,
  listConversationIntents,
  disambiguateConversationIntent,
  enableConversationIntent,
  disableConversationIntent,
  submitConversationInteraction,
} from '../../generated/api/sdk.gen.js';
import type {
  ListConversationsData,
  CreateConversationData,
  ConversationQuickStart,
  StartConversationFromMessageResponse,
  GetConversationData,
  UpdateConversationData,
  DeleteConversationData,
  ReplyToConversationData,
  CloseContextPanelData,
  ListConversationMessagesData,
  ListConversationMembersData,
  InviteToConversationData,
  RemoveConversationMemberData,
  ListConversationAttachmentsData,
  AttachToConversationData,
  DetachFromConversationData,
  SetConversationTypingData,
  ListConversationIntentsData,
  DisambiguateConversationIntentData,
  EnableConversationIntentData,
  DisableConversationIntentData,
  SubmitConversationInteractionData,
} from '../../generated/api/types.gen.js';

// The generated `*Data` types include a literal `url` field that the SDK
// helpers treat as optional (via `OmitKeys<..., 'url'>` inside `Options<T>`).
// Drop it at the facade so call sites don't need to pass the URL literal.
type NoUrl<T> = Omit<T, 'url'>;

export const conversationsApi = {
  // Core CRUD
  list: (params?: NoUrl<ListConversationsData>) => listConversations(params),
  create: (params: NoUrl<CreateConversationData>) => createConversation(params),
  // The generated SDK is `ThrowOnError = false`, so it RESOLVES `{ data, error, response }` on a
  // 4xx/5xx. This used to cast that envelope's `.data` and return it — on an error response that
  // cast produced `undefined` typed as a conversation, so `useStartFinanceConversation`'s
  // `onSuccess` ran and dereferenced `data.conversation.id`, while its `onError` (which shows the
  // user the failure) could never fire. `unwrapGenerated` turns the error branch into the throw
  // the caller is already written for, carrying the server's message and HTTP status.
  startFromMessage: (body: ConversationQuickStart): Promise<StartConversationFromMessageResponse> =>
    unwrapGenerated(startConversationFromMessage({ body })),
  get: (params: NoUrl<GetConversationData>) => getConversation(params),
  update: (params: NoUrl<UpdateConversationData>) => updateConversation(params),
  delete: (params: NoUrl<DeleteConversationData>) => deleteConversation(params),
  // Folders. Moving a conversation INTO one is `update({ body: { groupId } })` — it is a
  // property of the conversation, not an operation on the group.
  listGroups: () => listConversationGroups(),
  createGroup: (name: string) => createConversationGroup({ body: { name } }),
  deleteGroup: (id: string) => deleteConversationGroup({ path: { id } }),
  // Messages
  reply: (params: NoUrl<ReplyToConversationData>) => replyToConversation(params),
  submitInteraction: (
    conversationId: string | number,
    interactionId: string | number,
    body: NonNullable<SubmitConversationInteractionData['body']>,
  ) =>
    submitConversationInteraction({
      path: { id: String(conversationId), interactionId: String(interactionId) },
      body,
    } satisfies NoUrl<SubmitConversationInteractionData>),
  // Spec 76 Phase E — close-panel: server posts a system close-summary card
  // to the parent conversation; type/id are derived server-side from the
  // child's subjectType/subjectId so clients can't spoof them.
  closeContextPanel: (params: NoUrl<CloseContextPanelData>) => closeContextPanel(params),
  listMessages: (params: NoUrl<ListConversationMessagesData>) => listConversationMessages(params),
  // Members
  listMembers: (params: NoUrl<ListConversationMembersData>) => listConversationMembers(params),
  inviteMember: (params: NoUrl<InviteToConversationData>) => inviteToConversation(params),
  removeMember: (params: NoUrl<RemoveConversationMemberData>) => removeConversationMember(params),
  // Attachments
  listAttachments: (params: NoUrl<ListConversationAttachmentsData>) => listConversationAttachments(params),
  attach: (params: NoUrl<AttachToConversationData>) => attachToConversation(params),
  detach: (params: NoUrl<DetachFromConversationData>) => detachFromConversation(params),
  // Typing indicator (Spec 66 Phase F)
  setTyping: (params: NoUrl<SetConversationTypingData>) => setConversationTyping(params),
  // Intent classification (Spec 69 Phase 4)
  listIntents: (params: NoUrl<ListConversationIntentsData>) => listConversationIntents(params),
  disambiguateIntent: (params: NoUrl<DisambiguateConversationIntentData>) => disambiguateConversationIntent(params),
  disableIntent: (params: NoUrl<DisableConversationIntentData>) => disableConversationIntent(params),
  enableIntent: (params: NoUrl<EnableConversationIntentData>) => enableConversationIntent(params),
};
